import { Eye, Sparkles, Trash2 } from 'lucide-react';
import { apercuTexte, pluriel } from './formatConfig';
import ApercuExercice from './ApercuExercice';

export const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400';
const PLACE = { Fragile: 'avant le commun', 'En cours': 'après le commun', Acquis: 'après le commun' };

/**
 * Aperçu élève et retrait d'une ligne du panier. `nom` : ce que lisent les
 * lecteurs d'écran (« Aperçu {nom} », « Retirer {nom} »).
 */
export const ActionsLigne = ({ nom, onApercu, onRetirer }) => (
  <>
    <button type="button" onClick={onApercu} aria-label={`Aperçu ${nom}`} title="Aperçu élève" className={`text-gray-500 hover:text-gray-800 rounded flex-shrink-0 ${FOCUS}`}>
      <Eye size={13} />
    </button>
    <button type="button" onClick={onRetirer} aria-label={`Retirer ${nom}`} title="Retirer" className={`text-red-400 hover:text-red-600 rounded flex-shrink-0 ${FOCUS}`}>
      <Trash2 size={13} />
    </button>
  </>
);

/**
 * Marque F8 d'un exercice composé par TanoEd : l'icône seule, le mot en
 * infobulle et pour les lecteurs d'écran. `nb` : ligne mixte du panier (ajout à
 * la main sur la même carte), le compte s'affiche devant l'icône.
 */
export const MarqueProposee = ({ nb = null }) => {
  const libelle = nb ? `${nb} proposé${nb > 1 ? 's' : ''}` : 'proposé';
  return (
    <span role="img" aria-label={libelle} title={libelle} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-600 flex-shrink-0">
      {nb}
      <Sparkles size={12} aria-hidden="true" />
    </span>
  );
};

/**
 * Sections Fragile / En cours / Acquis du panier en mode groupes (R9 amendée le
 * 2026-10-03) : les exercices propres à chaque groupe s'y voient, s'ouvrent
 * (aperçu élève) et s'y retirent. On ajoute à un groupe depuis le catalogue
 * (« Ajouter à »), pas d'ici (2026-10-04). Le commun est la liste du
 * panier au-dessus. Composant de présentation : l'état vit dans `QCMDashboard`.
 * @param {{ groupes: Array<{ nom, propres: Array<{ id, payload }> }>,
 *   onApercu: (payload) => void, onRetirer: (nom, id) => void }} props
 */
const PanierGroupes = ({ groupes, onApercu, onRetirer }) => groupes.map((g) => (
    <div key={g.nom} role="group" tabIndex={-1} aria-label={`Panier, groupe ${g.nom}`} className="px-4 py-2 text-sm focus:outline-none">
      <h4 className="text-xs font-bold text-gray-700">
        {g.nom} <span className="font-normal text-gray-500">· {pluriel(g.propres.length, 'exercice')} en plus, {PLACE[g.nom]}</span>
      </h4>
      <ul className="mt-1 space-y-1" aria-label={`Exercices du groupe ${g.nom}`}>
        {g.propres.map(({ id, payload }) => (
          <li key={id} className="flex items-center gap-2">
            {payload?._meta?.origine === 'auto' && <MarqueProposee />}
            <ApercuExercice payload={payload} className="flex-1 min-w-0 text-gray-600" />
            <ActionsLigne
              nom={`${apercuTexte(payload)} du groupe ${g.nom}`}
              onApercu={() => onApercu(payload)}
              // La ligne retirée disparaît : le focus revient à son groupe.
              onRetirer={(e) => { e.currentTarget.closest('[role="group"]')?.focus(); onRetirer(g.nom, id); }}
            />
          </li>
        ))}
      </ul>
    </div>
));

export default PanierGroupes;
